import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type {
  BuildingInspection,
  Checklist,
  EquipmentActivity,
  EquipmentActivityType,
  HygieneFacilityCheck,
  PackingRecord,
  PestMonitoringCheck,
  Shipment,
  ShipmentCorrectiveAction,
  TrainingRecordType,
  TrainingSession,
  TrainingTopic,
  VisitorEntry,
} from './types'

// The farm-level logs (CanadaGAP Forms G, I, J, K, L, M, O, Q): one hook shape for all eight, the same as
// useHarvestLogs (logs / loading / error / addLog / updateLog). Rows are dated, not per field, so a
// page loads one year, optionally for one building / facility / equipment name.

type Opt<T> = T | null | undefined
type Base = { id: string }

/** Create inputs: optional fields may be null (cleared on edit, dropped on create). */
export type BuildingInspectionInput = {
  buildingName: string
  /** The inventory building, if picked from it; null unlinks on edit. */
  assetId?: Opt<string>
  inspectedOn: string
  completedBy?: Opt<string>
  checklist: Checklist
  interiorWorkNeeded?: Opt<string>
  interiorWorkDoneOn?: Opt<string>
  interiorWorkDoneBy?: Opt<string>
  interiorWorkOverseenBy?: Opt<string>
  exteriorWorkNeeded?: Opt<string>
  exteriorWorkDoneOn?: Opt<string>
  exteriorWorkDoneBy?: Opt<string>
  exteriorWorkOverseenBy?: Opt<string>
}
export type EquipmentActivityInput = {
  performedOn: string
  equipmentName: string
  /** The inventory equipment, if picked from it; null unlinks on edit. */
  assetId?: Opt<string>
  activityType: EquipmentActivityType
  description?: Opt<string>
  performedBy?: Opt<string>
}
export type HygieneFacilityCheckInput = {
  facilityName: string
  checkedOn: string
  checkedAt?: Opt<string>
  checklist: Checklist
  assessmentOk?: Opt<boolean>
  assessmentNotes?: Opt<string>
  cleanedBy?: Opt<string>
  cleanedByCompany?: boolean
}
export type TrainingSessionInput = {
  heldOn: string
  topics: TrainingTopic[]
  topicOther?: Opt<string>
  traineeCount?: Opt<number>
  traineeNames?: Opt<string>
  trainerName?: Opt<string>
  recordType?: Opt<TrainingRecordType>
  recordReference?: Opt<string>
}
export type VisitorEntryInput = {
  visitedOn: string
  visitorName: string
  companyName?: Opt<string>
  purpose?: Opt<string>
  location?: Opt<string>
  policyAcknowledged: boolean
}
export type PackingRecordInput = {
  packedOn: string
  handledByName?: Opt<string>
  productVariety?: Opt<string>
  harvestedOn?: Opt<string>
  fieldOrLotRef?: Opt<string>
  incomingLotId?: Opt<string>
  outgoingLotId?: Opt<string>
  quantity?: Opt<number>
  quantityUnit?: Opt<string>
  primaryPackaging?: Opt<string>
  secondaryPackaging?: Opt<string>
  packagingChecked?: Opt<boolean>
  storedOn?: Opt<string>
  rotatedAppropriately?: Opt<boolean>
  phiMet?: Opt<boolean>
  siteAssessed?: Opt<boolean>
}
export type ShipmentInput = {
  shippedOn: string
  productIdentifier?: Opt<string>
  quantity?: Opt<number>
  quantityUnit?: Opt<string>
  vehicleInspected?: Opt<boolean>
  vehicleIssue?: Opt<string>
  correctiveAction?: Opt<ShipmentCorrectiveAction>
  rotatedAppropriately?: Opt<boolean>
  truckId?: Opt<string>
  destination?: Opt<string>
  loaderName?: Opt<string>
}
export type PestMonitoringCheckInput = {
  buildingName: string
  assetId?: Opt<string>
  checkedOn: string
  deviceOrArea: string
  findings?: Opt<string>
  actionTaken?: Opt<string>
  personResponsible?: Opt<string>
}

/** The name filter's query parameter, for forms kept per building / facility / equipment. */
type NameParam = 'buildingName' | 'facilityName' | 'equipmentName'

export function useDatedLogs<T extends Base, Input extends object>(
  orgId: string | undefined,
  farmId: string | undefined,
  path: string,
  opts: { year: number; name?: string; nameParam?: NameParam },
) {
  const [logs, setLogs] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/${path}` : null
  const { year, name, nameParam } = opts

  const loadLogs = useCallback(async () => {
    if (!base) return
    setLoading(true)
    setError(null)
    try {
      const query = new URLSearchParams({ from: `${year}-01-01`, to: `${year}-12-31`, limit: '200' })
      if (name && nameParam) query.set(nameParam, name)
      const rows: T[] = []
      for (let cursor: string | null = null; ; ) {
        if (cursor) query.set('cursor', cursor)
        const page: { data: T[]; nextCursor: string | null } = await api.getPage<T>(`${base}?${query}`)
        rows.push(...page.data)
        if (!page.nextCursor) break
        cursor = page.nextCursor
      }
      setLogs(rows)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load records.')
    } finally {
      setLoading(false)
    }
  }, [base, year, name, nameParam])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  async function addLog(input: Input) {
    if (!base) return
    const created = await api.post<T>(base, dropNulls(input))
    setLogs((prev) => [created, ...prev])
    return created
  }

  /** Several entries in one go (a Form M round: one row per trap). */
  async function addLogs(inputs: Input[]) {
    if (!base) return
    const created: T[] = []
    for (const input of inputs) created.push(await api.post<T>(base, dropNulls(input)))
    setLogs((prev) => [...[...created].reverse(), ...prev])
    return created
  }

  async function updateLog(id: string, patch: Partial<Input>) {
    if (!base) return
    const updated = await api.patch<T>(`${base}/${id}`, patch)
    setLogs((prev) => prev.map((l) => (l.id === id ? updated : l)))
    return updated
  }

  return { logs, loading, error, addLog, addLogs, updateLog, reload: loadLogs }
}

type Args = [orgId: string | undefined, farmId: string | undefined, year: number, name?: string]

export const useBuildingInspections = (...[orgId, farmId, year, name]: Args) =>
  useDatedLogs<BuildingInspection, BuildingInspectionInput>(orgId, farmId, 'building-inspections', { year, name, nameParam: 'buildingName' })
export const useEquipmentActivities = (...[orgId, farmId, year, name]: Args) =>
  useDatedLogs<EquipmentActivity, EquipmentActivityInput>(orgId, farmId, 'equipment-activities', { year, name, nameParam: 'equipmentName' })
export const useHygieneFacilityChecks = (...[orgId, farmId, year, name]: Args) =>
  useDatedLogs<HygieneFacilityCheck, HygieneFacilityCheckInput>(orgId, farmId, 'hygiene-facility-checks', { year, name, nameParam: 'facilityName' })
export const useTrainingSessions = (...[orgId, farmId, year]: Args) =>
  useDatedLogs<TrainingSession, TrainingSessionInput>(orgId, farmId, 'training-sessions', { year })
export const useVisitorEntries = (...[orgId, farmId, year]: Args) =>
  useDatedLogs<VisitorEntry, VisitorEntryInput>(orgId, farmId, 'visitor-entries', { year })
export const usePestMonitoringChecks = (...[orgId, farmId, year, name]: Args) =>
  useDatedLogs<PestMonitoringCheck, PestMonitoringCheckInput>(orgId, farmId, 'pest-monitoring-checks', { year, name, nameParam: 'buildingName' })
export const usePackingRecords = (...[orgId, farmId, year]: Args) =>
  useDatedLogs<PackingRecord, PackingRecordInput>(orgId, farmId, 'packing-records', { year })
export const useShipments = (...[orgId, farmId, year]: Args) =>
  useDatedLogs<Shipment, ShipmentInput>(orgId, farmId, 'shipments', { year })
